import { useTranslation } from "react-i18next";
import Navigation from "../components/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import CookieNotice from "../components/CookieNotice";

const BookingTerms = () => {
  const { t } = useTranslation();
  const supportLinkEn = import.meta.env.VITE_STRIPE_SUPPORT_LINK_EN?.trim() || "";
  const supportLinkDa = import.meta.env.VITE_STRIPE_SUPPORT_LINK_DA?.trim() || "";
  const hasSupportLink = Boolean(supportLinkEn || supportLinkDa);

  return (
    <Navigation>
      {/* Language Switcher */}
      <LanguageSwitcher />

      {/* Cookie Notice */}
      <CookieNotice />

      <main className="min-h-screen bg-white px-4 py-10 sm:px-6 sm:py-12">
        <div className="container mx-auto max-w-3xl">
          {/* Header */}
          <div className="mb-8">
            <h1 className="mb-4 text-3xl font-bold text-[#006B5F] sm:text-4xl">
              {t('bookingTerms.title')}
            </h1>
            <p className="text-base leading-7 text-gray-600 sm:text-lg">
              {t('bookingTerms.subtitle')}
            </p>
          </div>

          {/* Intro */}
          <div className="mb-8">
            <p className="mb-4 text-base leading-7 text-gray-700">
              {t('bookingTerms.intro')}
            </p>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <p className="break-words text-sm leading-6 text-gray-700">
                {t('bookingTerms.company')}
              </p>
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {/* 1. Service */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.service.title')}
              </h2>
              <p className="text-base leading-7 text-gray-700">
                {t('bookingTerms.service.content')}
              </p>
            </section>

            {/* 2. Payment */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.payment.title')}
              </h2>
              <p className="text-base leading-7 text-gray-700">
                {t('bookingTerms.payment.content')}
              </p>
            </section>

            {/* 3. Cancellation */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.cancellation.title')}
              </h2>
              <p className="mb-3 text-base leading-7 text-gray-700">
                {t('bookingTerms.cancellation.content')}
              </p>
              <p className="text-sm italic leading-6 text-gray-600">
                {t('bookingTerms.cancellation.notice')}
              </p>
            </section>

            {/* 4. Confidentiality */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.confidentiality.title')}
              </h2>
              <p className="text-base leading-7 text-gray-700">
                {t('bookingTerms.confidentiality.content')}
              </p>
            </section>

            {/* 5. Withdrawal */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.withdrawal.title')}
              </h2>
              <p className="text-base leading-7 text-gray-700">
                {t('bookingTerms.withdrawal.content')}
              </p>
            </section>

            {/* 6. Technical */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.technical.title')}
              </h2>
              <p className="text-base leading-7 text-gray-700">
                {t('bookingTerms.technical.content')}
              </p>
            </section>

            {/* 7. Privacy */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.privacy.title')}
              </h2>
              <p className="mb-2 text-base leading-7 text-gray-700">
                {t('bookingTerms.privacy.content')}
              </p>
              <p className="text-sm leading-6 text-gray-600">
                {t('bookingTerms.privacy.more')}
              </p>
            </section>

            {/* 8. Contact */}
            <section>
              <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                {t('bookingTerms.contact.title')}
              </h2>
              <p className="break-words text-base leading-7 text-gray-700">
                {t('bookingTerms.contact.content')}
              </p>
            </section>

            {hasSupportLink && (
              <section>
                <h2 className="mb-3 text-xl font-bold text-[#006B5F] sm:text-2xl">
                  {t('bookingTerms.donations.title')}
                </h2>
                <p className="mb-3 text-base leading-7 text-gray-700">
                  {t('bookingTerms.donations.intro')}
                </p>
                <ul className="list-disc space-y-2 pl-5 text-base leading-7 text-gray-700 sm:pl-6">
                  {(t('bookingTerms.donations.items', { returnObjects: true }) as string[]).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="mt-4 text-base leading-7 text-gray-700">
                  {t('bookingTerms.donations.closing')}
                </p>
              </section>
            )}
          </div>

          {/* Footer note */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              © 2025 Omsorgsterapi (CVR: 45482278)
            </p>
          </div>
        </div>
      </main>
    </Navigation>
  );
};

export default BookingTerms;
